import { z } from "zod";
import { zId, zIsoDate, zMoney } from "./common.js";

export const GRANT_STAGES = ["researching", "loi", "applied", "awarded", "declined", "closed"] as const;
export const zGrantStage = z.enum(GRANT_STAGES);
export type GrantStage = z.infer<typeof zGrantStage>;

export const GRANT_DELIVERABLE_TYPES = ["loi", "application", "report", "site_visit", "acknowledgment", "other"] as const;
export const zGrantDeliverableType = z.enum(GRANT_DELIVERABLE_TYPES);
export type GrantDeliverableType = z.infer<typeof zGrantDeliverableType>;

export const zCreateGrant = z.object({
  funderId: zId,
  name: z.string().trim().min(1).max(300),
  purpose: z.string().max(10_000).optional(),
  amountRequested: zMoney.nullable().optional(),
  loiDeadline: zIsoDate.nullable().optional(),
  applicationDeadline: zIsoDate.nullable().optional(),
  decisionDate: zIsoDate.nullable().optional(),
  fundId: zId.nullable().optional(),
  campaignId: zId.nullable().optional(),
  ownerId: zId.nullable().optional(),
  notes: z.string().max(50_000).optional(),
});
export type CreateGrant = z.infer<typeof zCreateGrant>;

/** awarding happens through the dedicated award endpoint, not this patch */
export const zUpdateGrant = zCreateGrant.omit({ funderId: true }).partial().extend({
  stage: zGrantStage.exclude(["awarded"]).optional(),
});
export type UpdateGrant = z.infer<typeof zUpdateGrant>;

/** award → pledge with installments (reuses the pledge engine) */
export const zAwardGrant = z.object({
  amountAwarded: zMoney,
  fundId: zId.optional(),
  campaignId: zId.nullable().optional(),
  startDate: zIsoDate,
  frequency: z.enum(["monthly", "quarterly", "semiannual", "annual", "custom"]).default("annual"),
  installmentCount: z.number().int().min(1).max(360).default(1),
  customInstallments: z.array(z.object({ dueDate: zIsoDate, amount: zMoney })).optional(),
});
export type AwardGrant = z.infer<typeof zAwardGrant>;

export const zUpsertGrantDeliverable = z.object({
  title: z.string().trim().min(1).max(300),
  type: zGrantDeliverableType.default("other"),
  dueDate: zIsoDate.nullable().optional(),
  assigneeId: zId.nullable().optional(),
  /** when true and a due date is set, a linked task is created */
  createTask: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
});
export type UpsertGrantDeliverable = z.infer<typeof zUpsertGrantDeliverable>;
